import Controller from '@ember/controller';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import { RoomsOrderBy, SortDirection } from 'garaje/graphql/generated/roomba-types';
import type { PaginationInput, RoomsPageFilterInput, RoomsSortOrder } from 'garaje/graphql/generated/roomba-types';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type StateService from 'garaje/services/state';
import { GENERIC_RESOURCE_ICON } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import compact from 'lodash/compact';

export type ResourceType = {
  id: number;
  label: string;
  count: number;
  icon: string;
  beta?: boolean;
};

export default class ResourcesResourceTypeListController extends Controller {
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare roombaGraphql: RoombaGraphqlService;
  @service declare abilities: AbilitiesService;

  @tracked resourceTypes: ResourceType[] = [];

  loadResourceTypes = task({ drop: true }, async () => {
    const [desk, room, parking] = await Promise.all([
      this.getDeskResourceType.perform(),
      this.getRoomResourceType.perform(),
      this.getParkingResourceType.perform(),
    ]);

    this.resourceTypes = compact([desk, room, parking]);
  });

  getRoomResourceType = task({ drop: true }, async () => {
    if (this.abilities.cannot('visit roomba')) {
      return;
    }

    const resourceType = {
      id: 3,
      label: 'Room',
      icon: GENERIC_RESOURCE_ICON.ROOM,
      count: 0,
    };

    try {
      const sort: RoomsSortOrder = { by: RoomsOrderBy.DisplayName, direction: SortDirection.Asc };

      const filter: RoomsPageFilterInput = {
        locationId: { _eq: this.state.currentLocation.id, _in: null, _is_null: null, _neq: null, _nin: null },
        buildingId: null,
        buildingName: null,
        displayName: null,
        floorName: null,
      };

      const pagination: PaginationInput = {
        limit: 1,
        offset: 0,
      };

      const { totalCount } = await this.roombaGraphql.getRoomsPaginated(filter, sort, pagination);

      return {
        ...resourceType,
        count: totalCount || 0,
      };
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      return resourceType;
    }
  });

  getDeskResourceType = task({ drop: true }, async () => {
    if (this.abilities.cannot('visit all desks desks')) {
      return;
    }

    const resourceType = {
      id: 1,
      label: 'Desk',
      icon: GENERIC_RESOURCE_ICON.DESK,
      count: 0,
    };

    try {
      const deskLocations = await this.store.query('desk-location', {
        reload: true,
        filter: {
          'location-id': this.state.currentLocation.id,
        },
      });

      return {
        ...resourceType,
        count: deskLocations?.slice()?.[0]?.desksQuantity || 0,
      };
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      return resourceType;
    }
  });

  getParkingResourceType = task({ drop: true }, async () => {
    if (this.abilities.cannot('use parking resource')) {
      return;
    }

    const resourceType = {
      id: 2,
      label: 'Parking',
      icon: GENERIC_RESOURCE_ICON.PARKING,
    };

    try {
      const locationId = this.state.currentLocation.id;
      const resourcesUrl = urlBuilder.rms.resources(locationId, {
        'resource-type-id': '2',
        limit: 10000,
        offset: 0,
      });

      const response = await this.ajax.request<{ data: unknown[] }>(resourcesUrl, {
        type: 'GET',
        headers: { Accept: 'application/vnd.api+json' },
      });

      return {
        ...resourceType,
        count: response?.data?.length || 0,
      };
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      return {
        ...resourceType,
        count: 0,
      };
    }
  });
}
